import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';

import {
  reportCollectionsFilterSelector,
  reportFilterCountryCodeSelector,
  reportFilterSelector,
  reportPettyCashDifferenceSelector,
  downloadReportPdf,
} from '../../state';
import { PettyCashDifference } from '../../types';
import { getAccountingInformationReportPdfFilters } from '../AccountingInformationReportDataGrids/getPdfFilters';
import handlerFactory, { PettyCashDifferenceDataGridContext } from './handlerFactory';

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<
    PettyCashDifference,
    PettyCashDifferenceDataGridContext
  >();

  const { t } = useTranslation();
  const title = t('petty-cash-difference.title');
  const country = useSelector(reportFilterCountryCodeSelector);
  const pettyCashDifferenceResource = useSelector(reportPettyCashDifferenceSelector);
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);

  const { data, loading } = pettyCashDifferenceResource;

  const gridContext = useMemo(
    () => ({
      t,
      country,
    }),
    [t, country]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title,
    loading,
    filters: reportCollectionsFilters,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<
    PettyCashDifference,
    PettyCashDifferenceDataGridContext
  >();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reportFilters = useSelector(reportFilterSelector);
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);

  useEffect(() => {
    handler.setExportConfig({
      PDF: {
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: [
                'countryCode',
                'stores',
                'pos',
                'salesTypes',
                'segments',
                'timeBand',
                'hourRange',
                'startBusinessDate',
                'endBusinessDate',
                'groupBy',
              ],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
              filters: getAccountingInformationReportPdfFilters(
                reportFilters,
                reportCollectionsFilters
              ),
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t, reportFilters, reportCollectionsFilters]);

  return null;
};

const PettyCashDifferenceDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory}>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default PettyCashDifferenceDataGrid;
